import type { PublicId } from 'flow/store/engine/types';

// ============== Rule Tree Helper Definitions ============== //

export const ruleType = {
  group: 'group',
  threshold: 'threshold',
} as const;

export type RuleType = keyof typeof ruleType;

export const groupType = {
  and: 'and',
  or: 'or',
} as const;

export type GroupType = keyof typeof groupType;

/*
* The path of groups leading up to a rule
*   eg. "the 0th child of an 'and' group"
*/
export interface RulePathSegment {
  groupType: GroupType;
  index: number;
}

export type RulePath = RulePathSegment[];

/* The insertion position for a rule, relative to another rule */
export const ruleInsertionPosition = {
  after: 'after',
  before: 'before',
  firstChild: 'firstChild',
  lastChild: 'lastChild',
} as const;

export type RuleInsertionPosition = keyof typeof ruleInsertionPosition;

// ============== Rule Tree Definitions ============== //
export type RuleBuilderRule = GroupRule | ThresholdRule;

export interface BaseRule {
  id: PublicId;
  type: RuleType;
  data: {
    path: RulePath;
  };
}

/*
 * An interior tree node in a Rules Tree.
 * Always has children.
 */
export interface GroupRule extends BaseRule {
  // id: unique id that is generated when GroupRule is built
  type: (typeof ruleType)['group'];
  data: BaseRule['data'] & {
    children: RuleBuilderRule[];
    groupType: GroupType;
    isAmbiguous: boolean;
  };
}

/* A leaf node in a Rules Tree */
export interface ThresholdRule extends BaseRule {
  // id: threshold id
  type: (typeof ruleType)['threshold'];
  data: BaseRule['data'];
}

/** The root of a rule tree - it is always a group rule. */
export type RuleTree = GroupRule;
